/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import classnames from 'classnames';
import * as React from 'react';

import { Button } from '..';
import Collapse from '../collapse/Collapse';
import { ICollapseProps } from '../collapse/Collapse.props';
import { IDrawerProps } from './Drawer.props';

/**
 * Drawer State Interface
 */
export interface IDrawerState {
    isOpen: boolean;
    buttonText?: string;
}

/**
 * Glyph placement type.
 */
export type GlyphPlacement = 'start' | 'end';

/**
 * Drawer - This is a full width uncontrolled collapse where the button glyph changes
 * when the drawer is open and closed.
 */

export default class Drawer extends React.PureComponent<IDrawerProps, IDrawerState> {
    public state: IDrawerState;

    private readonly drawerOpenGlyph: string;

    private readonly drawerCloseGlyph: string;

    private readonly drawerGlyphClass: string;

    private isManuallyToggled: boolean;

    constructor(props: IDrawerProps) {
        super(props);
        this._toggle = this._toggle.bind(this);
        this.isManuallyToggled = false;
        this.state = { isOpen: props.collapseProps?.isOpen ?? false, buttonText: '' };
        this.drawerGlyphClass = classnames(
            'drawer__glyph',

            this.props.toggleGlyphClassName ? this.props.toggleGlyphClassName : ''
        );

        if (this.props.animateGlyph) {
            this.drawerOpenGlyph = classnames(
                this.drawerGlyphClass,

                this.props.openGlyph ? this.props.openGlyph : this.props.closeGlyph ? this.props.closeGlyph : ''
            );
            this.drawerCloseGlyph = this.drawerOpenGlyph;
        } else {
            this.drawerOpenGlyph = classnames(
                this.drawerGlyphClass,

                this.props.openGlyph ? this.props.openGlyph : ''
            );
            this.drawerCloseGlyph = classnames(
                this.drawerGlyphClass,

                this.props.closeGlyph ? this.props.closeGlyph : ''
            );
        }
    }

    public render(): JSX.Element {
        const collapseProps = { ...this.props.collapseProps } as ICollapseProps;

        // If the drawer is manually toggled by pressing the button, then we use the internal state
        if (collapseProps.isOpen === undefined || this.isManuallyToggled) {
            collapseProps.isOpen = this.state.isOpen;
        } else {
            this.setState({
                isOpen: collapseProps.isOpen
            });
        }
        collapseProps.children = this.props.children;
        const drawerClass = classnames(
            'drawer',

            this.props.className ? this.props.className : ''
        );
        this.isManuallyToggled = false;
        return (
            <div className={drawerClass} role={this.props.role}>
                <Button
                    {...this.props.toggleButtonProps}
                    onClick={this._toggle}
                    aria-expanded={collapseProps.isOpen}
                    block
                    aria-label={this.props.ariaLabel}
                    disabled={this.props.disabled}
                    className="drawer__button"
                    color="secondary"
                >
                    {this._generateDrawerButtonInnerMarkup(collapseProps.isOpen)}
                </Button>
                <Collapse {...collapseProps} />
            </div>
        );
    }

    /**
     * Method generate drawer inner markup.
     * @param collapseState -Drawer button collapse state.
     * @returns Jsx element.
     */
    private _generateDrawerButtonInnerMarkup(collapseState: boolean): JSX.Element {
        const drawerButtonTextClass = classnames(
            'drawer__buttontext',

            this.props.toggleButtonClassName ? this.props.toggleButtonClassName : ''
        );
        if (this.props.glyphPlacement === 'end') {
            return (
                <>
                    <span className={classnames(drawerButtonTextClass, '__start')}>{this.props.toggleButtonText}</span>
                    {this._generateGlyphMarkup('drawer__glyph__end', collapseState)}
                </>
            );
        }

        if (this.props.glyphPlacement === 'start') {
            return (
                <>
                    {this._generateGlyphMarkup('drawer__glyph__start', collapseState)}
                    <span className={classnames(drawerButtonTextClass, '__end')}>{this.props.toggleButtonText}</span>
                </>
            );
        }
        return <span className={classnames(drawerButtonTextClass, '__start')}>{this.props.toggleButtonText}</span>;
    }

    /**
     * Method generate glyph markup.
     * @param location -Glyph icon location.
     * @param collapseState -Drawer button collapse state.
     * @returns Jsx element.
     */
    private _generateGlyphMarkup(location: string, collapseState: boolean): JSX.Element {
        let glyphClass = collapseState ? this.drawerOpenGlyph : this.drawerCloseGlyph;
        glyphClass = `${glyphClass} ${location}`;
        return <span className={glyphClass} />;
    }

    /**
     * Method on click of drawer button.
     * @param e -Mouse event for drawer button.
     */
    private _toggle(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
        e.preventDefault();
        this.isManuallyToggled = true;

        if (this.props.onToggle) {
            this.setState(
                { isOpen: !this.state.isOpen, buttonText: e.currentTarget.innerText },
                () => this.props.onToggle && this.props.onToggle(this.state)
            );
        } else {
            this.setState({ isOpen: !this.state.isOpen });
        }
    }
}
